import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, PanInfo } from "framer-motion";
import { Check, X } from "lucide-react";
import toast from "react-hot-toast";
import { serverErrorMessage } from "../../../utils/serverError";
import { Button } from "./atoms";
import { useLifeAreas } from "./config";
import { EffortIcon } from "./icons";
import { createStep } from "./ringsApi";
import { fs, ink, T, withAlpha } from "./tokens";
import { DimensionKey, RingNextStep, RingSnapshot } from "./types";
import { useReducedMotion } from "./useReducedMotion";

const SWIPE_PX = 90;
const FADE_MS = 600;

type Props = {
  steps: RingNextStep[];
  snapshot: RingSnapshot;
  onAct: (id: string, action: "complete" | "dismiss") => Promise<void>;
  onCreated: () => void;
};

const inputStyle: React.CSSProperties = {
  ...fs("sm"),
  background: T.color.bg.card,
  color: T.color.text.primary,
  border: `1px solid ${T.color.bg.overlay}`,
  borderRadius: T.radius.lg,
  padding: "8px 12px",
};

const StepCard: React.FC<{ step: RingNextStep; onAct: Props["onAct"] }> = ({ step, onAct }) => {
  const reduced = useReducedMotion();
  const [state, setState] = useState<"idle" | "done" | "gone">("idle");
  const L = useLifeAreas();
  const { t } = useTranslation();
  const area = L.area(step.dimensionKey);
  const effortLabel = L.config.efforts[step.effort] ?? step.effort;
  const title = L.stepTitle(step);
  const reason = L.stepReason(step);

  const act = async (action: "complete" | "dismiss") => {
    if (state !== "idle") return;
    if (action === "complete") {
      setState("done"); // checkmark, then fade after 600ms
      await new Promise((r) => setTimeout(r, reduced ? 0 : FADE_MS));
    }
    setState("gone");
    await onAct(step.id, action);
  };

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x > SWIPE_PX) act("complete");
    else if (info.offset.x < -SWIPE_PX) act("dismiss");
  };

  if (state === "gone") return null;

  return (
    <motion.li
      layout={!reduced}
      drag={reduced ? false : "x"}
      dragSnapToOrigin
      dragElastic={0.4}
      onDragEnd={onDragEnd}
      animate={{ opacity: state === "done" ? 0.4 : 1 }}
      transition={{ duration: reduced ? 0 : 0.3 }}
      aria-label={t("lifeAreas.steps.aria", { area: area.label, title, effort: effortLabel })}
      style={{
        listStyle: "none",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        background: T.color.bg.card,
        border: `1px solid ${T.color.bg.overlay}`,
        borderLeft: `3px solid ${area.color}`,
        borderRadius: T.radius.xl,
        color: T.color.text.primary,
      }}
    >
      <span
        title={effortLabel}
        style={{
          width: 32,
          height: 32,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: T.radius.full,
          background: T.color.bg.overlay,
          color: T.color.text.secondary,
        }}
      >
        <EffortIcon effort={step.effort} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            ...fs("sm", 600),
            display: "block",
            textDecoration: state === "done" ? "line-through" : "none",
          }}
        >
          {title}
        </span>
        <span style={{ ...fs("xs"), display: "block", color: T.color.text.secondary }}>
          {area.emoji} {area.label} · {effortLabel}
        </span>
        {step.description && (
          <span style={{ ...fs("xs"), display: "block", marginTop: 2, color: T.color.text.secondary }}>
            {step.description}
          </span>
        )}
        {reason && (
          <span style={{ ...fs("xs"), display: "block", marginTop: 6, color: T.color.text.secondary }}>
            <span style={{ fontWeight: 600, color: T.color.text.primary }}>{t("lifeAreas.steps.why")}</span>
            {reason}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={() => act("dismiss")}
        aria-label={t("lifeAreas.steps.dismiss", { title })}
        className="lg2-btn"
        style={{
          padding: 6,
          borderRadius: T.radius.full,
          border: "none",
          background: "transparent",
          color: T.color.text.secondary,
          cursor: "pointer",
        }}
      >
        <X size={16} />
      </button>
      <button
        type="button"
        onClick={() => act("complete")}
        aria-label={t("lifeAreas.steps.complete", { title })}
        className="lg2-btn"
        style={{
          padding: 6,
          borderRadius: T.radius.full,
          border: `1px solid ${withAlpha(T.color.semantic.success, 0.4)}`,
          background: withAlpha(T.color.semantic.success, 0.15),
          color: ink(T.color.semantic.success),
          cursor: "pointer",
        }}
      >
        <Check size={16} />
      </button>
    </motion.li>
  );
};

// Spec organism AstriaActionPlan: priority actions, complete / dismiss.
const NextSteps: React.FC<Props> = ({ steps, snapshot, onAct, onCreated }) => {
  const L = useLifeAreas();
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [dimKey, setDimKey] = useState<DimensionKey>(L.areas[0]?.key ?? "body");
  const [title, setTitle] = useState("");

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await createStep(dimKey, title.trim());
      setTitle("");
      setAdding(false);
      onCreated();
    } catch (err: any) {
      toast.error(serverErrorMessage(err, "lifeAreas.steps.addFailed"));
    }
  };

  return (
    <section aria-label={t("lifeAreas.steps.title")} id="life-gps-next-steps" style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 style={{ ...fs("base", 600), margin: 0 }}>{t("lifeAreas.steps.title")}</h3>
        <button
          type="button"
          onClick={() => setAdding((a) => !a)}
          style={{
            ...fs("xs", 600),
            color: T.color.text.link,
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          {adding ? t("lifeAreas.steps.cancel") : t("lifeAreas.steps.addOwn")}
        </button>
      </div>

      {adding && (
        <form onSubmit={add} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          <select
            value={dimKey}
            onChange={(e) => setDimKey(e.target.value as DimensionKey)}
            aria-label={t("lifeAreas.steps.area")}
            style={inputStyle}
          >
            {L.areas
              .filter((a) => snapshot.dimensions.find((d) => d.key === a.key)?.isActive)
              .map((a) => (
                <option key={a.key} value={a.key}>
                  {a.label}
                </option>
              ))}
          </select>
          <input
            value={title}
            maxLength={L.config.limits.stepTitleMax}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("lifeAreas.steps.placeholder")}
            aria-label={t("lifeAreas.steps.titleLabel")}
            style={{ ...inputStyle, flex: 1, minWidth: 0 }}
          />
          <Button type="submit" size="sm">
            {t("lifeAreas.steps.add")}
          </Button>
        </form>
      )}

      {steps.length === 0 ? (
        <p
          style={{
            ...fs("sm"),
            margin: 0,
            padding: 16,
            textAlign: "center",
            color: T.color.text.secondary,
            background: T.color.bg.card,
            borderRadius: T.radius.xl,
          }}
        >
          {t("lifeAreas.steps.empty")}
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, display: "grid", gap: 8 }}>
          {steps.map((s) => (
            <StepCard key={s.id} step={s} onAct={onAct} />
          ))}
        </ul>
      )}
    </section>
  );
};

export default NextSteps;
